import { cn } from "@utils/cn.ts";
import type * as type from "@vTypes";
import { useMemo } from "react";

import Card_date from "./Card_date";
import type { AvoidEntriesByDate } from "@/pages/roster-wanted/utils/wantedEntries";

interface CalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_calendar {
  [date: string]: CalendarEntry;
}
function TableSelectDate({
  activeDate,
  holidayDates,
  availableShifts,
  selectedCalendarData,
  setSelectedCalendarData,
  submittedStatus,

  selectedShiftType,

  isExistWanted,
  myWeeklyOff,
  enableCommentInput = false,
  dailyMaxRequests,
  dailyRequestCounts,
  avoidShiftByDate,
  calendarDisplay = "all",
  activeIntent,
  onAvoidShiftRemove,
  onDateClick,
  onMissingShiftSelection,
  onLockedDateClick,
}: {
  activeDate: { year: number; month: number };
  holidayDates: string[];
  availableShifts: type.Type_availableShifts[];
  selectedCalendarData: Type_calendar;
  setSelectedCalendarData: (
    value: Type_calendar | ((prev: Type_calendar) => Type_calendar)
  ) => void;
  submittedStatus: string;

  selectedShiftType: string;
  isExistWanted: boolean;
  myWeeklyOff?: number | null;
  enableCommentInput?: boolean; // 원티드 작성 > 캔린더에 사유 입력을 위함.
  dailyMaxRequests?: Record<string, number>;
  dailyRequestCounts?: Record<string, number>;
  avoidShiftByDate?: AvoidEntriesByDate;
  calendarDisplay?: "all" | "wanted" | "avoid";
  activeIntent?: "wanted" | "avoid" | null;
  onAvoidShiftRemove?: (dateStr: string) => void;
  onDateClick?: (
    dateStr: string,
    intent: "wanted" | "avoid",
    comment?: string,
    shiftId?: string
  ) => void;
  onMissingShiftSelection?: () => void;
  onLockedDateClick?: (dateStr: string) => void;
}) {
  const firstDay = new Date(activeDate.year, activeDate.month - 1, 1);
  const lastDay = new Date(activeDate.year, activeDate.month, 0);
  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay(); // 한 주 시작 : 일
  startDay = (startDay + 6) % 7; // 한 주 시작 : 월

  const displayData = useMemo(() => {
    const data: Type_calendar = { ...selectedCalendarData };
    if (myWeeklyOff != null) {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(activeDate.year, activeDate.month - 1, day);
        const dayOfWeek = (date.getDay() + 6) % 7; // 월(0) ~ 일(6)
        if (dayOfWeek === myWeeklyOff) {
          const dateStr = `${activeDate.year}-${String(
            activeDate.month
          ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          data[dateStr] = { type: "주", comment: "주휴" };
        }
      }
    }
    return data;
  }, [
    selectedCalendarData,
    myWeeklyOff,
    daysInMonth,
    activeDate.year,
    activeDate.month,
  ]);

  const weeks: React.JSX.Element[][] = [];
  let currentWeek: React.JSX.Element[] = [];

  for (let i = 0; i < startDay; i++) {
    const prevDate = new Date(firstDay);
    prevDate.setDate(prevDate.getDate() - (startDay - i));
    const day = prevDate.getDate();
    const weekend = prevDate.getDay() % 7;

    currentWeek.push(
      <td
        className={`other-month ${weekend === 6 ? "calendar_sat" : weekend === 0 ? "calendar_sun" : ""}`}
        key={`prev-${i}`}
      >
        <div className="calendar-date">{day}</div>
      </td>
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const shift = displayData ? displayData[dateStr] : undefined;
    const weekend = (day + startDay) % 7;

    const isHol = holidayDates.includes(dateStr);
    currentWeek.push(
      <td key={dateStr}>
        <Card_date
          day={day}
          holiday={{ weekend: weekend, isHol: isHol }}
          dateStr={dateStr}
          availableShifts={availableShifts}
          shift={shift}
          setSelectedCalendarData={setSelectedCalendarData}
          selectedShiftType={selectedShiftType}
          submittedStatus={submittedStatus}
          isExistWanted={isExistWanted}
          enableCommentInput={enableCommentInput}
          maxCount={dailyMaxRequests?.[dateStr]}
          currentCount={dailyRequestCounts?.[dateStr] || 0}
          avoidShiftCode={avoidShiftByDate?.[dateStr]?.shiftId}
          avoidComment={avoidShiftByDate?.[dateStr]?.comment}
          calendarDisplay={calendarDisplay}
          activeIntent={activeIntent}
          onAvoidShiftRemove={onAvoidShiftRemove}
          onDateClick={onDateClick}
          onMissingShiftSelection={onMissingShiftSelection}
          onLockedDateClick={onLockedDateClick}
        />
      </td>
    );

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  const remainingCells = 7 - currentWeek.length;

  if (remainingCells > 0) {
    for (let index = 0; index < remainingCells; index += 1) {
      currentWeek.push(
        <td key={`next-${index}`} aria-hidden="true" className="other-month" />
      );
    }
    weeks.push(currentWeek);
  }

  return (
    <table className="w_myWanted__calendar w_myWanted__selectCalendar min-w-[34rem] w-full">
      <thead>
        <tr>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[13px]"
            )}
          >
            월
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[13px]"
            )}
          >
            화
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[13px]"
            )}
          >
            수
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[13px]"
            )}
          >
            목
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[13px]"
            )}
          >
            금
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sat !text-[13px]"
            )}
          >
            토
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sun !text-[13px]"
            )}
          >
            일
          </th>
        </tr>
      </thead>
      <tbody className="calendar-body">
        {weeks.map((week) => (
          <tr key={String(week[0]?.key)}>{week}</tr>
        ))}
      </tbody>
    </table>
  );
}

export default TableSelectDate;
